import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  Alert,
  AccountCard,
  EmptyState,
  Button,
  Dropdown,
  Icon,
  ibanToBbanCz,
  Spinner,
  SegmentGroup,
  Switch,
} from '@penta/penta-shared';
import styles from '../../dashboard.module.scss';
import { Trans, useTranslation } from 'react-i18next';
import { usePentaRq } from '@penta/penta-api';
import { useCallback, useMemo } from 'react';
import { useClient } from '../../../contexts/ClientContext';
import { MessageList } from '../../../components/Messages/MessageList';
import {
  AccountList,
  type AccountListItem,
} from '../../../components/AccountList';
import { useRedirectToOldIb } from '../../../hooks/useRedirectToOldIb';
import { useDashboardViewPreference } from '../../../hooks/useDashboardViewPreference';
import { FEATURES } from '../../../constants/featureFlags';
import {
  isProductCreationEnabled,
  type CreatableProduct,
} from '../../../utils/envUtils';
import { DEPOSIT_INFO_URL } from '../../../constants/constants';

const DEPOSIT_INFO_COMPONENTS = { strong: <strong /> };

export const Route = createFileRoute('/$locale/smartbanking/dashboard')({
  component: Overview,
});

export function Overview() {
  const { t, i18n } = useTranslation();
  useParams({ from: '/$locale/smartbanking/dashboard' });
  const api = usePentaRq();
  const { selectedClientId, user, detailedClientInfo } = useClient();
  const { redirect: redirectToOldIb } = useRedirectToOldIb();
  const {
    view,
    allClients: storedAllClients,
    currency,
    setView,
    setAllClients,
    setCurrency,
  } = useDashboardViewPreference(user?.id);

  // Účty všech firem smí vidět jen klient, který na to má oprávnění; chodí
  // jako akce v ClientInfo. Dokud ClientInfo nedorazí, tváříme se, že právo
  // není — přepínač pak nezabliká a hlavně se nepošle allClients=true dřív,
  // než víme, že na to uživatel má.
  const canSeeAllAccounts =
    detailedClientInfo?.actions?.includes('CanSeeAllAccounts') ?? false;

  // Uložená volba se bez oprávnění ignoruje: uživatel ho mohl mít dřív, nebo
  // ho má u jiné firmy, a bez tohoto by se dotaz na produkty ptal na něco,
  // k čemu nemá přístup.
  const allClients = canSeeAllAccounts && storedAllClients;

  // Fetch product data (accounts, cards, etc.)
  const {
    data: productData,
    isPending,
    error,
  } = api.useQuery(
    'get',
    '/product',
    {
      params: {
        // Vynechané clientId = účty všech firem, ke kterým má uživatel přístup.
        query: allClients ? {} : { clientId: selectedClientId ?? undefined },
      },
    },
    { enabled: selectedClientId !== null },
  );

  const {
    data: portfolioData,
    isPending: isPortfolioPending,
    error: portfolioError,
  } = api.useQuery(
    'get',
    '/portfolio/overview/{clientId}',
    {
      params: {
        path: {
          clientId: selectedClientId ?? -1,
        },
      },
    },
    {
      enabled: FEATURES.ENABLE_PORTFOLIO && selectedClientId !== null,
    },
  );

  // Helper function to format currency with proper locale
  const formatCurrency = useCallback(
    (amount: number, currency: string) => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
      }).format(amount);
    },
    [i18n.language],
  );

  const formatAccountNumber = useCallback((iban: string) => {
    const bbanResult = ibanToBbanCz(iban);
    return bbanResult.isValid ? bbanResult.formattedBban : iban;
  }, []);

  const getCurrentAccountActions = useCallback(
    (productId: number) => [
      {
        'data-testid': `btn-pay-${productId}`,
        label: t('accounts.pay'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('SepaPaymentOrder', productId),
      },
      {
        'data-testid': `btn-history-${productId}`,
        label: t('accounts.history'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('TurnoverList', productId),
      },
      {
        'data-testid': `btn-orders-${productId}`,
        label: t('accounts.standingOrders'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('StandingOrderList'),
      },
    ],
    [t, redirectToOldIb],
  );

  const getTermAccountActions = useCallback(
    (productId: number) => [
      {
        'data-testid': `btn-statement-${productId}`,
        label: t('accounts.statement'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('StatementList'),
      },
      {
        'data-testid': `btn-history-${productId}`,
        label: t('accounts.history'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('TurnoverList'),
      },
    ],
    [t, redirectToOldIb],
  );

  const getCreditAccountActions = useCallback(
    (productId: number) => [
      {
        'data-testid': `btn-detail-${productId}`,
        label: t('accounts.detail'),
        variant: 'outline' as const,
        onClick: () => redirectToOldIb('AccountLoanList', productId),
      },
    ],
    [t, redirectToOldIb],
  );

  const getPortfolioActions = useCallback(
    (portfolioId?: number, portfolioType?: string) => {
      if (!portfolioId) return [];

      let redirectCode = 'PortfolioOverview';
      if (portfolioType === 'CBR') redirectCode = 'PortfolioBRC';
      else if (portfolioType === 'PWM')
        redirectCode = 'PortfolioPWMInstruments';
      else if (portfolioType === 'PPI') redirectCode = 'PortfolioPPI';

      return [
        {
          'data-testid': `btn-detail-${portfolioId}`,
          label: t('accounts.detail'),
          variant: 'outline' as const,
          onClick: () => redirectToOldIb(redirectCode),
        },
      ];
    },
    [t, redirectToOldIb],
  );

  const allCurrentAccounts = productData?.currentAccounts || [];
  const allSavingsAccounts = productData?.savingsAccounts || [];
  const allTermAccounts = productData?.termAccounts || [];
  const allCreditAccounts = productData?.creditAccounts || [];
  const portfolios = portfolioData?.portfolioTypeOverview || [];

  // Nabídka měn se odvozuje z účtů, které klient opravdu má — natvrdo psaný
  // výčet by u klienta bez eurového účtu nabízel prázdný výsledek.
  const availableCurrencies = useMemo(() => {
    const codes = new Set<string>();
    [
      allCurrentAccounts,
      allSavingsAccounts,
      allTermAccounts,
      allCreditAccounts,
    ].forEach((group) =>
      group.forEach((account) => {
        if (account.currency) codes.add(account.currency);
      }),
    );
    return [...codes].sort();
  }, [
    allCurrentAccounts,
    allSavingsAccounts,
    allTermAccounts,
    allCreditAccounts,
  ]);

  // Uložená měna může pocházet z doby, kdy klient takový účet ještě měl (nebo
  // z přepnutí firmy). Neplatná volba se chová jako „všechny měny“, aby
  // seznam nezůstal záhadně prázdný.
  const activeCurrency = availableCurrencies.includes(currency) ? currency : '';

  const filterByCurrency = useCallback(
    <T extends { currency?: string }>(accounts: T[]): T[] =>
      activeCurrency
        ? accounts.filter((account) => account.currency === activeCurrency)
        : accounts,
    [activeCurrency],
  );

  const currentAccounts = filterByCurrency(allCurrentAccounts);
  const savingsAccounts = filterByCurrency(allSavingsAccounts);
  const termAccounts = filterByCurrency(allTermAccounts);
  const creditAccounts = filterByCurrency(allCreditAccounts);

  // S účty všech firem musí být z titulku poznat, komu účet patří; jinak dává
  // smysl název produktu.
  const itemTitle = useCallback(
    (account: { name: string; owner?: string }) =>
      allClients ? (account.owner ?? account.name) : account.name,
    [allClients],
  );

  const toListItems = useCallback(
    (
      accounts: {
        id: number;
        name: string;
        owner?: string;
        iban?: string;
        currency: string;
        currentBalance: number;
        availableBalance?: number;
      }[],
    ): AccountListItem[] =>
      accounts.map((account) => ({
        id: account.id,
        title: itemTitle(account),
        description: account.iban
          ? (formatAccountNumber(account.iban) ?? account.iban)
          : '',
        // Disponibilní zůstatek se předává tak, jak ho API vrátilo. Produkty
        // bez něj (vklady, úvěry) ho nemají čím nahradit — účetní zůstatek
        // není totéž a v UI se proto místo něj ukáže pomlčka.
        availableBalance: account.availableBalance,
        bookBalance: account.currentBalance,
        currency: account.currency,
      })),
    [itemTitle, formatAccountNumber],
  );

  const currencyLabel = useCallback(
    (code: string) =>
      code
        ? t('dashboard.currencyOne', { currency: code })
        : t('dashboard.currencyAll'),
    [t],
  );

  const currencyItems = useMemo(
    () => [
      { id: '', label: currencyLabel('') },
      ...availableCurrencies.map((code) => ({
        id: code,
        label: currencyLabel(code),
      })),
    ],
    [availableCurrencies, currencyLabel],
  );

  const viewOptions = useMemo(
    () => [
      { value: 'standard', label: t('dashboard.viewStandard') },
      { value: 'compact', label: t('dashboard.viewCompact') },
    ],
    [t],
  );

  // V režimu všech firem odpověď neříká, které firmě produkt patří, takže akci
  // nelze do legacy IB odeslat se správným clientId — poslal by se kontext
  // aktuálně vybrané firmy k účtu firmy jiné. Dokud API to pole nedodá,
  // se zapnutým přepínačem se akce nad účtem nenabízejí.
  const currentActions = allClients ? undefined : getCurrentAccountActions;
  const termActions = allClients ? undefined : getTermAccountActions;
  const creditActions = allClients ? undefined : getCreditAccountActions;

  // Ze stejného důvodu se nenabízí ani založení produktu: žádost míří vždy do
  // jedné firmy a v seznamu přes všechny firmy není z čeho ji určit.
  const createAction = (
    product: CreatableProduct,
    pageIdentifier: string,
    testId: string,
  ) =>
    allClients || !isProductCreationEnabled(product) ? undefined : (
      <Button
        variant="outline"
        onClick={() => redirectToOldIb(pageIdentifier)}
        data-testid={testId}
      >
        {t('dashboard.termDepositsCreate')}
      </Button>
    );

  return (
    <div className={styles.dashboardContent}>
      <Alert
        variant="info"
        className={styles.depositInfo}
        data-testid="deposit-info"
      >
        <span className={styles.depositInfoContent}>
          <span>
            <Trans
              i18nKey="dashboard.depositInfo"
              components={DEPOSIT_INFO_COMPONENTS}
            />
          </span>
          <a
            href={DEPOSIT_INFO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.depositInfoLink}
            data-testid="deposit-info-link"
          >
            {t('dashboard.depositInfoLink')}
            <span className={styles.srOnly}>
              {` ${t('dashboard.depositInfoNewTab')}`}
            </span>
          </a>
        </span>
      </Alert>

      <section>
        <header>
          <h1 className="h2">{t('dashboard.overview')}</h1>
        </header>

        <div className={styles.viewControls}>
          {canSeeAllAccounts ? (
            <Switch
              checked={allClients}
              onCheckedChange={setAllClients}
              label={t('dashboard.showAllClients')}
              data-testid="toggle-all-clients"
            />
          ) : (
            // Prázdné místo drží zbytek řádku u pravého okraje i bez přepínače
            <span />
          )}
          {/* Dle návrhu tvoří výběr měny a volba zobrazení jednu skupinu
              u pravého okraje; přepínač firem stojí sám vlevo. */}
          <div className={styles.viewControlsRight}>
            {/* Výběr měny zužuje seznam na účty v dané měně. Číselník se skládá
                z měn, které klient reálně má (API filtr neumí, děláme ho tady),
                a volba se pamatuje per uživatel, takže přežije reload. */}
            <div className={styles.currencyFilter}>
              <Dropdown
                trigger={
                  <Button
                    variant="outline"
                    iconRight={
                      <Icon name="chevron-down" size={16} aria-hidden />
                    }
                    data-testid="currency-filter-trigger"
                  >
                    {currencyLabel(activeCurrency)}
                  </Button>
                }
                items={currencyItems}
                value={activeCurrency}
                onValueChange={setCurrency}
                ariaLabel={t('dashboard.currencyLabel')}
                data-testid="currency-filter"
              />
            </div>
            {/* Segment (radiogroup), ne Tabs — volba přepíná seznam účtů pod
                ovládacím řádkem, ne panel uvnitř přepínače. Popisek je jen pro
                čtečky: dle návrhu stojí v řádku samotný přepínač bez
                viditelného textu. */}
            <SegmentGroup
              options={viewOptions}
              value={view}
              onValueChange={(value) =>
                setView(value === 'compact' ? 'compact' : 'standard')
              }
              aria-label={t('dashboard.viewLabel')}
              data-testid="view-segment"
            />
          </div>
        </div>

        {isPending ? (
          <div className={styles.loadingWrapper}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <Alert variant="error" data-testid="error-products">
            {t('dashboard.errorLoading')}
          </Alert>
        ) : (
          <>
            <header>
              <h2 className="h3">{t('dashboard.deposits')}</h2>
            </header>
            <section className={styles.accountGroup}>
              {currentAccounts.length === 0 ? (
                <EmptyState
                  title={t('dashboard.currentAccountsEmpty')}
                  action={createAction(
                    'currentAccount',
                    'CurrentAccountRequest',
                    'btn-create-current-account',
                  )}
                  data-testid="current-accounts-empty-state"
                />
              ) : (
                <AccountList
                  items={toListItems(currentAccounts)}
                  variant={view}
                  testIdPrefix="account-card-current"
                  getActions={currentActions}
                  formatCurrency={formatCurrency}
                />
              )}
            </section>

            <>
              <header>
                <h2 className="h3">{t('dashboard.savingsAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {savingsAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.savingsEmpty')}
                    data-testid="savings-empty-state"
                  />
                ) : (
                  <AccountList
                    items={toListItems(savingsAccounts)}
                    variant={view}
                    testIdPrefix="account-card-savings"
                    getActions={currentActions}
                    formatCurrency={formatCurrency}
                  />
                )}
              </section>
            </>

            <>
              <header>
                <h2 className="h3">{t('dashboard.termAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {termAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.termDepositsEmpty')}
                    action={createAction(
                      'termDeposit',
                      'TimeDepositRequestNew',
                      'btn-create-term-deposit',
                    )}
                    data-testid="term-deposits-empty-state"
                  />
                ) : (
                  <>
                    <AccountList
                      items={toListItems(termAccounts)}
                      variant={view}
                      testIdPrefix="account-card-term"
                      getActions={termActions}
                      descriptionLabel={t('dashboard.columns.contractNumber')}
                      formatCurrency={formatCurrency}
                    />
                    {isProductCreationEnabled('termDeposit') && (
                      <EmptyState
                        title={t(
                          'dashboard.termDepositsRequestNew',
                          'Žádost o nový termínovaný vklad',
                        )}
                        action={createAction(
                          'termDeposit',
                          'TimeDepositRequestNew',
                          'btn-create-term-deposit',
                        )}
                        data-testid="term-deposits-new-state"
                      />
                    )}
                  </>
                )}
              </section>
            </>

            <>
              <header>
                <h2 className="h3">{t('dashboard.creditAccounts')}</h2>
              </header>

              <section className={styles.accountGroup}>
                {creditAccounts.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.creditEmpty')}
                    data-testid="credit-empty-state"
                  />
                ) : (
                  <AccountList
                    items={toListItems(creditAccounts)}
                    variant={view}
                    testIdPrefix="account-card-credit"
                    getActions={creditActions}
                    formatCurrency={formatCurrency}
                  />
                )}
              </section>
            </>
          </>
        )}

        {FEATURES.ENABLE_PORTFOLIO && (
          <>
            <header>
              <h2 className="h3">{t('dashboard.portfolio', 'Portfolio')}</h2>
            </header>

            {isPortfolioPending ? (
              <div className={styles.loadingWrapper}>
                <Spinner size="lg" />
              </div>
            ) : portfolioError ? (
              <Alert variant="error" data-testid="error-portfolio">
                {t('dashboard.errorLoading')}
              </Alert>
            ) : (
              <section className={styles.accountGroup}>
                {portfolios.length === 0 ? (
                  <EmptyState
                    title={t('dashboard.portfolioEmpty')}
                    data-testid="portfolio-empty-state"
                  />
                ) : (
                  portfolios.map((item, index) => (
                    <AccountCard
                      key={item.portfolioId || index}
                      data-testid={`account-card-portfolio-${item.portfolioId || index}`}
                      title={item.name || ''}
                      description={item.portfolioType || ''}
                      amount={formatCurrency(item.amount || 0, item.currency)}
                      unit=""
                      subvalue={`${t('accounts.accountBalance')}: ${formatCurrency(item.amount || 0, item.currency)}`}
                      actions={getPortfolioActions(
                        item.portfolioId,
                        item.portfolioType,
                      )}
                    />
                  ))
                )}
              </section>
            )}
          </>
        )}
      </section>

      <aside aria-label={t('dashboard.news')}>
        <h2 className="h3">{t('dashboard.news')}</h2>
        <MessageList />
      </aside>
    </div>
  );
}
